// ─────────────────────────────────────────────
//  Surgeons domain — the `surgeons` collection is ATC's own record of the
//  surgeons the department works with (name, phone, email, specialty), owned
//  and edited in-app by an administrator. Team directory v2 §1.3.
//
//  ⚠️ THIS COLLECTION IS AUTHORITATIVE. There were THREE surgeon lists; the
//  consolidation is done and this is the one a human edits (Setup → Facility →
//  Surgeons, src/pages/setupPage.surgeons.js).
//
//    surgeons/{id}                      HERE. Per-record documents, so a
//                                       surgeon can carry an email and a
//                                       `listed` opt-out and be gated by a rule
//                                       of their own.
//    procedure_refs/{fid}.surgeons[]    DERIVED, and still live: the flat NAMES
//                                       the AssignmentGrid's SURGEON typeahead
//                                       reads, plus whatever a doctor typed
//                                       into a case row. The Setup panel
//                                       mirrors each name into it with
//                                       arrayUnion / arrayRemove, so case entry
//                                       is untouched and the remembered names
//                                       survive.
//    procedure_refs/{fid}.surgeonList[] RETIRED. Nothing writes it; the Setup
//                                       panel reads it once to offer a one-time
//                                       import of what a facility already had.
//
//  An ARRAY FIELD ON A FACILITY-SCOPED DOCUMENT could not carry a per-record
//  flag, could not be gated independently of the scheduling data every board
//  reads, and was rewritten wholesale on every edit. That is why the roster
//  moved rather than the collection being added beside it.
//
//  Same normalizer contract as domain/staff.js: this mapper WHITELISTS fields.
//  A field it does not name is silently dropped and renders empty downstream.
// ─────────────────────────────────────────────
import { contactEmail, contactPhone, contactPhoneDisplay, filterListed } from "./contact";

/**
 * Map ONE raw surgeon doc → the shape the directory renders.
 * Returns null for an unusable record (no id), same as normalizeStaff.
 */
export function normalizeSurgeon(surgeon) {
  if (!surgeon || !surgeon.id) return null;
  return {
    id: surgeon.id,
    name: surgeon.name || "",
    phone: contactPhone(surgeon),
    phoneDisplay: contactPhoneDisplay(surgeon),
    email: contactEmail(surgeon),
    specialty: surgeon.specialty || "",
    facilityId: surgeon.facilityId || null,
    // Absent means listed — coerced to a strict boolean HERE so every consumer
    // can read it plainly instead of re-learning the `!== false` rule.
    listed: surgeon.listed !== false,
    _surgeon: true,
  };
}

/**
 * Normalize a raw surgeon list, drop unusable rows and the ones that opted
 * out, and sort by name so the section reads alphabetically.
 */
// ONE guard, not two: a `= []` default parameter plus an Array.isArray check
// makes the check's false branch unreachable from a no-arg call, which reads as
// a coverage hole and hides which of the two actually protects the caller. The
// isArray guard is the one that matters — it also catches null and a snapshot
// that came back as an object.
export function listedSurgeons(surgeonDocs) {
  const rows = (Array.isArray(surgeonDocs) ? surgeonDocs : [])
    .map(normalizeSurgeon)
    .filter(Boolean);
  return filterListed(rows).sort((a, b) => a.name.localeCompare(b.name));
}
